import { SubscriptionPurchaseBackdrop } from "@/components/subscription/subscription-purchase-backdrop";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { OutlinedButton } from "@/components/ui/buttons/OutlinedButton";
import { useSubscriptionOfferings } from "@/features/settings/hooks/use-subscription-products";
import { usePurchaseAndVerify } from "@/features/paywall/hooks/use-purchase-and-verify";
import { useLogout } from "@/hooks/use-logout";
import { isPaywallOpenAtom } from "@/stores/paywall.store";
import { getSafeAreaBottom } from "@/utils/safe-area-bottom";
import { useIonRouter } from "@ionic/react";
import { PACKAGE_TYPE } from "@revenuecat/purchases-capacitor";
import { useSetAtom } from "jotai";
import { useState } from "react";
import { LegalLinksFooter } from "../legal-links-footer";
import { usePaywallViewed } from "../../analytics";

const FALLBACK_MONTHLY_PRICE = "£9.99";
const FALLBACK_YEARLY_PRICE = "£59";
const FALLBACK_YEARLY_SAVINGS = 45;

export default function ExpiredTrialPaywall() {
  usePaywallViewed("expired_trial");
  const router = useIonRouter();
  const setIsPaywallOpen = useSetAtom(isPaywallOpenAtom);
  const { logout } = useLogout();

  const { offerings, isLoading } = useSubscriptionOfferings();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const {
    isPurchasing,
    isBackdropOpen,
    backdropState,
    startPurchase,
    handleManualRetry,
    handleBackdropCancel,
  } = usePurchaseAndVerify({
    placement: "expired_trial",
    logPrefix: "[Paywall]",
    onUnlocked: () => {
      setIsPaywallOpen(false);
      router.push("/overview", "none", "replace");
    },
  });

  const availablePackages = offerings?.current?.availablePackages ?? [];
  const yearlyPackage = availablePackages.find((p) => p.packageType === PACKAGE_TYPE.ANNUAL);
  const monthlyPackage = availablePackages.find((p) => p.packageType === PACKAGE_TYPE.MONTHLY);

  const monthlyPrice = monthlyPackage?.product.priceString ?? FALLBACK_MONTHLY_PRICE;
  const yearlyPrice = yearlyPackage?.product.priceString ?? FALLBACK_YEARLY_PRICE;

  const yearlySavings =
    yearlyPackage?.product.price && monthlyPackage?.product.price
      ? Math.round((1 - yearlyPackage.product.price / (monthlyPackage.product.price * 12)) * 100)
      : FALLBACK_YEARLY_SAVINGS;

  const handleLogout = async () => {
    if (isLoggingOut || isPurchasing) return;
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error("[Paywall] Logout failed", error);
      setIsLoggingOut(false);
    }
  };

  const purchaseDisabled = isPurchasing || isLoggingOut || isLoading;

  return (
    <div className="fixed inset-0 z-[9999] flex justify-center overflow-hidden bg-[#271746] bg-[url('/assets/images/paywall-bg.webp')] bg-cover bg-bottom bg-no-repeat text-white">
      <div className="safe-area-top relative z-10 flex h-full w-full max-w-[450px] flex-col overflow-x-hidden overflow-y-auto px-9">
        {/* Log Out */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleLogout}
            disabled={isLoggingOut || isPurchasing}
            className="p-2 text-[13px] font-medium text-white underline disabled:opacity-60"
          >
            Log Out
          </button>
        </div>

        {/* Headline */}
        <h1
          className="mt-20 text-center text-[42px] leading-[40px] text-white"
          style={{ fontFamily: "var(--font-crimson-text)", fontWeight: 600 }}
        >
          Your <span className="text-[#DDC5FF] italic">free trial</span>
          <br />
          has ended
        </h1>

        {/* Subhead + body */}
        <p className="mt-[120px] text-center text-[32px] font-semibold text-[#DDC5FF]">
          Stay on plan
        </p>
        <p className="mt-4 text-center text-base leading-[26px] font-medium text-white">
          Keep using NutriCoach
          <br />
          for {monthlyPrice}/ month
        </p>

        {/* Flexible spacer */}
        <div className="min-h-10 flex-1" />

        {/* Footer */}
        <div
          className="flex flex-col items-center gap-5"
          style={{ paddingBottom: `calc(${getSafeAreaBottom()} + 1.5rem)` }}
        >
          <div className="flex w-full flex-col items-center gap-3">
            <p className="text-center text-[13px] leading-normal font-medium text-white">
              Choose yearly and save {yearlySavings}%.
            </p>
            <FilledButton
              color="white"
              borderRadius="wide"
              textCase="normal"
              onClick={() => startPurchase(yearlyPackage)}
              disabled={purchaseDisabled || !yearlyPackage}
            >
              Continue for {yearlyPrice}/year
            </FilledButton>
          </div>

          <OutlinedButton
            color="white"
            borderRadius="wide"
            textCase="normal"
            onClick={() => startPurchase(monthlyPackage)}
            disabled={purchaseDisabled || !monthlyPackage}
          >
            Continue for&nbsp;<span className="font-bold">{monthlyPrice}</span>/ month
          </OutlinedButton>

          <LegalLinksFooter
            idPrefix="expired-trial-paywall"
            className="!pt-0 text-white"
            linkClassName="text-white hover:text-purple-100"
          />
        </div>
      </div>

      <SubscriptionPurchaseBackdrop
        isOpen={isBackdropOpen}
        state={backdropState}
        onRetry={handleManualRetry}
        onCancel={handleBackdropCancel}
      />
    </div>
  );
}
